import { OSDImageViewer, OSDSlideViewer } from "../component/viewer";
import { useState, useEffect, useRef, useMemo, useCallback, createContext, Dispatch, SetStateAction } from "react";
import {
  getUserCustomSetting,
  patchUserCustomSetting,
  UserCustomSetting,
} from "../api/user";
import styled from "styled-components";
import {
  InfoCircleOutlined,
  CommentOutlined,
  FileImageOutlined,
  UpOutlined,
  DownOutlined,
  SwapOutlined,
  SplitCellsOutlined,
  LinkOutlined,
  CloseOutlined,
  MinusCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import SideMenuSample2 from "../component/viewer/SideMenuSample2";
import ImageSideMenu from "../component/viewer/ImageSideMenu";
import { Tag, Tooltip, Dropdown, message, Button, Segmented } from "antd";
import type { MenuProps } from "antd";
import { useRecoilState } from "recoil";
import {
  currentPostAtom,
  currentSlideAtom,
  viewerSlidesAtom,
  viewerImagesAtom,
  activeViewerIdAtom,
  isDualViewerAtom,
  isZoomSyncAtom,
  ViewerId,
  ViewerImage,
  ViewerMediaKind,
} from "../atom";
import {
  Slide,
  getSlideData,
  postAnnotation,
  getPhotoData,
  postImageAnnotation,
  getGrossAnnotationList,
  postGrossAnnotation,
} from "../api/slideAPI";
import {
  MediaGroup,
  MediaType,
  createMediaGroup,
  getMediaGroups,
  moveMediaToGroup,
} from "../api/mediaGroupAPI";
import {
  GroupFilter,
  UNGROUPED_FILTER,
  buildMembership,
  countByGroup,
  groupIdOf,
  matchesGroupFilter,
  mediaIdOf,
} from "../component/post/mediaGroup";
import { MediaGroupAssignModal } from "../component/post";
import { PostCase, getPostCaseList, postCaseDisplayName } from "../api/postCaseAPI";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getSlideMpp } from "../api/deepzoomAPI";
import { GrossImage, Photo, getGrossImageList, getImageList, getPostData } from "../api/postAPI";
import { getLoginUser } from "../api/user";
import { linkViewerZoom, useRegisteredViewers } from "../component/viewer/viewerRegistry";

export const ViewerContext = createContext<{
  userCustomSet: UserCustomSetting;
  setUserCustomSet: Dispatch<SetStateAction<UserCustomSetting>>;
  activePanel?: string | null;
  setActivePanel?: Dispatch<SetStateAction<string | null>>;
  togglePanel?: (panelName: string) => void;
}>({
  userCustomSet: {
    useScaleBar: true,
    useMagnificationBar: true,
    useCaptureButton: true,
    useViewerRotationSlider: true,
    useGammaSlider: true,
    useViewerThumbnail: true,
    useAnnotation: true,
    useICCButton: true,
    userGammaValue: 1,
    useInvertedZoom: false,
  },
  setUserCustomSet: () => {},
  activePanel: null,
  setActivePanel: () => {},
  togglePanel: () => {},
});

/** 하단 목록 탭별 미디어 그룹 타입 */
const MEDIA_TYPE_OF: Record<ViewerMediaKind, MediaType> = {
  slide: "SLIDE",
  image: "PHOTO",
  gross: "GROSS",
};
const MEDIA_TAB_LABEL: Record<ViewerMediaKind, string> = {
  slide: "Slide",
  image: "Image",
  gross: "Gross",
};
const MEDIA_ITEM_LABEL: Record<ViewerMediaKind, string> = {
  slide: "슬라이드",
  image: "이미지",
  gross: "그로스 이미지",
};
/** URL 에서 각 종류의 id 를 싣는 쿼리 이름 */
const MEDIA_ID_PARAM: Record<ViewerMediaKind, string> = {
  slide: "slideId",
  image: "photoId",
  gross: "grossPhotoId",
};
const MEDIA_KINDS: ViewerMediaKind[] = ["slide", "image", "gross"];

const parseMediaKind = (value: string | null): ViewerMediaKind =>
  value === "image" || value === "gross" ? value : "slide";

const mediaIdOfKind = (item: any, kind: ViewerMediaKind): number | undefined =>
  mediaIdOf(item, MEDIA_TYPE_OF[kind]);

const mediaNameOf = (item: any, kind: ViewerMediaKind): string =>
  (kind === "slide" ? item?.slidename : kind === "image" ? item?.imageName : item?.caption) || "";

const mediaThumbOf = (item: any, kind: ViewerMediaKind) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (kind === "slide") return `${base}/api/photo/${item?.thumbnail}`;
  if (kind === "image") return `${base}/api/photo/${item?.pathFileName}`;
  return `${base}/api/gross-photo/file/${item?.pathFileName}`;
};

const toViewerImage = (item: any, kind: "image" | "gross"): ViewerImage => ({
  kind,
  id: mediaIdOfKind(item, kind)!,
  pathFileName: item.pathFileName,
  annotations: null,
});

// 이미지/그로스 어노테이션 API (ViewerDisplay 와 동일)
const getImageAnnotations = async (photoId: number) => {
  const data = await getPhotoData(photoId);
  return Array.isArray(data) ? data : [];
};
const saveImageAnnotation = async (photoId: number, path: any, name: string) => {
  await postImageAnnotation(photoId, path, name);
};
const getGrossAnnotations = async (grossPhotoId: number) => {
  const data = await getGrossAnnotationList(grossPhotoId);
  return Array.isArray(data) ? data : [];
};
const saveGrossAnnotation = async (grossPhotoId: number, path: any, name: string) => {
  await postGrossAnnotation(grossPhotoId, path, name);
};

const Viewer = () => {
  const [activePanel, setActivePanel] = useState<string | null>(null);
  const [isSlideListOpen, setIsSlideListOpen] = useState<boolean>(true);
  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);
  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);

  const [viewerSlides, setViewerSlides] = useRecoilState(viewerSlidesAtom);
  const [viewerImages, setViewerImages] = useRecoilState(viewerImagesAtom);
  const [activeViewerId, setActiveViewerId] = useRecoilState(activeViewerIdAtom);
  const [isDualViewer, setIsDualViewer] = useRecoilState(isDualViewerAtom);
  const [isZoomSync, setIsZoomSync] = useRecoilState(isZoomSyncAtom);
  const registeredViewers = useRegisteredViewers();

  // 듀얼 뷰어 줌 동기화: 양쪽 뷰어가 모두 떠 있을 때만 연결.
  // 연결 시 활성 뷰어의 배율에 반대편을 맞춘다. (활성 뷰어가 바뀌는 것만으로는 다시 맞추지 않음)
  const activeViewerIdRef = useRef(activeViewerId);
  activeViewerIdRef.current = activeViewerId;
  useEffect(() => {
    const left = registeredViewers.left;
    const right = registeredViewers.right;
    if (!isDualViewer || !isZoomSync || !left || !right) return;
    const source = activeViewerIdRef.current === "right" ? right : left;
    return linkViewerZoom(left, right, source);
  }, [isDualViewer, isZoomSync, registeredViewers.left, registeredViewers.right]);

  const [slides, setSlides] = useState<Slide[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [grossPhotos, setGrossPhotos] = useState<GrossImage[]>([]);
  /** 하단 목록 탭 (Slide / Image / Gross) */
  const [mediaTab, setMediaTab] = useState<ViewerMediaKind>("slide");
  const [activeGroupFilter, setActiveGroupFilter] = useState<GroupFilter>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // 미디어 그룹(/api/media-groups) — 전 종류를 받아 탭별로 나눠 쓴다
  const [mediaGroups, setMediaGroups] = useState<MediaGroup[]>([]);
  const [groupChangeTarget, setGroupChangeTarget] = useState<{
    kind: ViewerMediaKind;
    id: number;
  } | null>(null);

  const [cases, setCases] = useState<PostCase[]>([]);
  const [activeCaseId, setActiveCaseId] = useState<number | null>(null);

  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const slideListRef = useRef<HTMLDivElement>(null);
  const slideCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [userCustomSet, setUserCustomSet] = useState<UserCustomSetting>({
    useScaleBar: true,
    useMagnificationBar: true,
    useCaptureButton: true,
    useViewerRotationSlider: true,
    useGammaSlider: true,
    useViewerThumbnail: true,
    useAnnotation: true,
    useICCButton: true,
    userGammaValue: 1,
    useInvertedZoom: false,
  });

  const activeSlide = viewerSlides[activeViewerId];
  const activeImage = viewerImages[activeViewerId];
  /** 활성 칸에 떠 있는 미디어 종류/id (칸마다 슬라이드 또는 이미지 중 하나) */
  const activeKind: ViewerMediaKind | null = activeImage
    ? activeImage.kind
    : activeSlide
    ? "slide"
    : null;
  const activeMediaId: number | null = activeImage
    ? activeImage.id
    : activeSlide?.slideId ?? null;

  const mediaListOf = useCallback(
    (kind: ViewerMediaKind): any[] =>
      kind === "slide" ? slides : kind === "image" ? photos : grossPhotos,
    [slides, photos, grossPhotos]
  );
  const tabList = mediaListOf(mediaTab);

  useEffect(() => {
    const getUserSetting = async () => {
      const userSet = await getUserCustomSetting();
      return setUserCustomSet(userSet);
    };

    getUserSetting();

    getLoginUser()
      .then((u) => {
        setIsAdmin(u?.role === "ADMIN_MAIN");
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const isInitialLoad = Object.keys(userCustomSet).length === 0;
    if (isInitialLoad) return;

    const timer = setTimeout(async () => {
      try {
        await patchUserCustomSetting(userCustomSet);
      } catch (error) {
        console.error("Failed to auto-save user custom settings:", error);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [userCustomSet]);

  useEffect(() => {
    setCurrentSlide(activeSlide as any);
  }, [activeSlide, setCurrentSlide]);

  // 이미지(Image/Gross)는 어노테이션 탭만 쓴다 — 이미지가 활성화되면 다른 탭을 닫는다
  useEffect(() => {
    if (activeImage && activePanel !== null && activePanel !== "annotation") {
      setActivePanel(null);
    }
  }, [activeImage, activePanel]);

  const initialSearchParamsRef = useRef(searchParams);
  useEffect(() => {
    const loadPost = async () => {
      if (id) {
        // 다른 게시글에서 남은 이미지 칸 정리 (슬라이드 칸은 아래에서 덮어쓴다)
        setViewerImages({ left: null, right: null });

        const [data, caseList, photoList, grossList, groups] = await Promise.all([
          getPostData(+id),
          getPostCaseList(+id),
          getImageList(+id),
          getGrossImageList(+id),
          getMediaGroups(+id),
        ]);
        setCurrentPost(data);
        setCases(caseList);

        const caseParam = initialSearchParamsRef.current.get("postCaseId");
        if (caseParam !== null && caseParam !== "") {
          const parsed = Number(caseParam);
          if (!Number.isNaN(parsed) && caseList.some((c) => c.postCaseId === parsed)) {
            setActiveCaseId(parsed);
          } else {
            setActiveCaseId(null);
          }
        } else {
          setActiveCaseId(null);
        }

        const slideList: Slide[] = data?.slides ?? [];
        const lists: Record<ViewerMediaKind, any[]> = {
          slide: slideList,
          image: Array.isArray(photoList) ? photoList : [],
          gross: Array.isArray(grossList) ? grossList : [],
        };
        setSlides(lists.slide);
        setPhotos(lists.image);
        setGrossPhotos(lists.gross);
        setMediaGroups(groups);

        // 요청한 종류(media)가 비어 있으면 슬라이드 → 이미지 → 그로스 순으로 있는 것을 연다
        const requested = parseMediaKind(initialSearchParamsRef.current.get("media"));
        const kind = [requested, ...MEDIA_KINDS].find((k) => lists[k].length > 0);
        if (!kind) return;
        setMediaTab(kind);

        const list = lists[kind];
        const idParam =
          kind === requested ? initialSearchParamsRef.current.get(MEDIA_ID_PARAM[kind]) : null;
        const target =
          (idParam && list.find((item) => mediaIdOfKind(item, kind) === +idParam)) || list[0];

        if (kind === "slide") {
          try {
            const freshData = await getSlideData(target.slideId);
            const merged = { ...target, ...freshData };
            setViewerSlides((prev) => ({ ...prev, left: merged }));
          } catch {
            setViewerSlides((prev) => ({ ...prev, left: target }));
          }
        } else {
          setViewerSlides((prev) => ({ ...prev, left: null }));
          setViewerImages((prev) => ({ ...prev, left: toViewerImage(target, kind) }));
        }
        setActiveViewerId("left");
      }
    };
    loadPost();
  }, [id]);

  const filteredEntries = useMemo(() => {
    let entries = tabList.map((item, i) => ({ item, originalIndex: i }));
    if (activeCaseId !== null) {
      entries = entries.filter(({ item }) => item.postCaseId === activeCaseId);
    }

    return entries.filter(({ item }) => matchesGroupFilter(item, activeGroupFilter));
  }, [tabList, activeCaseId, activeGroupFilter]);

  const countInCase = useCallback(
    (kind: ViewerMediaKind) => {
      const list = mediaListOf(kind);
      return activeCaseId === null
        ? list.length
        : list.filter((m: any) => m.postCaseId === activeCaseId).length;
    },
    [mediaListOf, activeCaseId]
  );

  const itemsInCase = useMemo(() => {
    if (activeCaseId === null) return tabList;
    return tabList.filter((m: any) => m.postCaseId === activeCaseId);
  }, [tabList, activeCaseId]);

  // 그룹별 개수는 목록 API 가 주지 않으므로 현재 탭·case 범위의 미디어에서 센다
  const groupCounts = useMemo(() => countByGroup(itemsInCase), [itemsInCase]);
  const groupsOf = useCallback(
    (kind: ViewerMediaKind) => mediaGroups.filter((g) => g.mediaType === MEDIA_TYPE_OF[kind]),
    [mediaGroups]
  );
  const tabGroups = groupsOf(mediaTab);
  const hasGroups = tabGroups.length > 0;
  const availableKinds = MEDIA_KINDS.filter((k) => mediaListOf(k).length > 0);

  /** 활성 칸에 띄운 미디어를 URL 에 반영 (새로고침·공유·브레드크럼용) */
  const syncMediaToUrl = useCallback(
    (item: any, kind: ViewerMediaKind) => {
      const params = new URLSearchParams(searchParams);
      MEDIA_KINDS.forEach((k) => params.delete(MEDIA_ID_PARAM[k]));
      if (kind === "slide") params.delete("media");
      else params.set("media", kind);
      params.set(MEDIA_ID_PARAM[kind], String(mediaIdOfKind(item, kind)));
      // 브레드크럼은 slideName / slideIndex 를 라벨로 쓴다 (종류와 무관)
      params.set("slideName", mediaNameOf(item, kind));
      const foundIndex = mediaListOf(kind).findIndex(
        (m) => mediaIdOfKind(m, kind) === mediaIdOfKind(item, kind)
      );
      if (foundIndex !== -1) {
        params.set("slideIndex", (foundIndex + 1).toString());
      }
      navigate(`/viewer/${id}?${params.toString()}`, { replace: true });
    },
    [searchParams, mediaListOf, navigate, id]
  );

  /** 미디어를 지정한 칸에 띄운다. 한 칸에는 슬라이드 또는 이미지 중 하나만 둔다. */
  const loadMediaIntoViewer = useCallback(
    async (item: any, kind: ViewerMediaKind, viewerId: ViewerId) => {
      if (kind === "slide") {
        let merged: Slide = item;
        try {
          const freshData = await getSlideData(item.slideId);
          merged = { ...item, ...freshData };
        } catch {
          // noop
        }
        setViewerImages((prev) => ({ ...prev, [viewerId]: null }));
        setViewerSlides((prev) => ({ ...prev, [viewerId]: merged }));
      } else {
        setViewerSlides((prev) => ({ ...prev, [viewerId]: null }));
        setViewerImages((prev) => ({ ...prev, [viewerId]: toViewerImage(item, kind) }));
      }

      if (viewerId === activeViewerId) {
        syncMediaToUrl(item, kind);
      }
    },
    [activeViewerId, syncMediaToUrl, setViewerSlides, setViewerImages]
  );

  /** 현재 탭의 미디어를 활성 칸에 띄운다 */
  const handleMediaClick = useCallback(
    (item: any) => {
      loadMediaIntoViewer(item, mediaTab, activeViewerId);
    },
    [loadMediaIntoViewer, mediaTab, activeViewerId]
  );

  /** 활성 칸 미디어의 목록 데이터 (그룹 소속 확인용) */
  const activeListItem = activeKind
    ? mediaListOf(activeKind).find((m) => mediaIdOfKind(m, activeKind) === activeMediaId)
    : undefined;

  const handleTabChange = (kind: ViewerMediaKind) => {
    if (kind === mediaTab) return;
    setMediaTab(kind);
    // 그룹은 종류별로 따로라 필터를 초기화한다 (case 필터는 유지)
    setActiveGroupFilter(null);
    const params = new URLSearchParams(searchParams);
    params.delete("mediaGroupId");
    navigate(`/viewer/${id}?${params.toString()}`, { replace: true });
  };

  const handleCaseFilterChange = (caseId: number | null) => {
    setActiveCaseId(caseId);

    const params = new URLSearchParams(searchParams);
    if (caseId !== null) {
      params.set("postCaseId", String(caseId));
    } else {
      params.delete("postCaseId");
    }
    params.delete("mediaGroupId");
    setActiveGroupFilter(null);
    navigate(`/viewer/${id}?${params.toString()}`, { replace: true });

    // 보고 있는 미디어가 현재 탭 종류인데 case 에서 빠지면 첫 항목으로 옮긴다
    if (activeKind !== mediaTab) return;
    const targetItems =
      caseId === null ? tabList : tabList.filter((m: any) => m.postCaseId === caseId);

    const stillVisible = targetItems.some(
      (m) => mediaIdOfKind(m, mediaTab) === activeMediaId
    );

    if (!stillVisible && targetItems.length > 0) {
      handleMediaClick(targetItems[0]);
    }
  };

  const handleGroupFilterChange = (group: GroupFilter) => {
    setActiveGroupFilter(group);

    if (activeKind === mediaTab && !matchesGroupFilter(activeListItem, group)) {
      const candidates =
        activeCaseId === null
          ? tabList
          : tabList.filter((m: any) => m.postCaseId === activeCaseId);
      const firstItem = candidates.find((m) => matchesGroupFilter(m, group));
      if (firstItem) {
        handleMediaClick(firstItem);
        return;
      }
    }

    const params = new URLSearchParams(searchParams);
    if (group != null) {
      params.set("mediaGroupId", String(group));
    } else {
      params.delete("mediaGroupId");
    }
    navigate(`/viewer/${id}?${params.toString()}`, { replace: true });
  };

  const handleMediaClickWithScroll = (
    item: any,
    index: number,
    event?: React.MouseEvent
  ) => {
    const isModifierClick = !!(event && (event.ctrlKey || event.metaKey));

    if (isModifierClick) {
      if (!isDualViewer) {
        setIsDualViewer(true);
        loadMediaIntoViewer(item, mediaTab, "right");
        setActiveViewerId("right");
      } else {
        const targetViewerId: ViewerId = activeViewerId === "left" ? "right" : "left";
        loadMediaIntoViewer(item, mediaTab, targetViewerId);
        setActiveViewerId(targetViewerId);
      }
    } else {
      handleMediaClick(item);
    }

    const cardElement = slideCardRefs.current[index];
    if (cardElement) {
      cardElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  };

  const toggleDualViewer = () => {
    if (isDualViewer) {
      setIsDualViewer(false);
      setViewerSlides((prev) => ({ ...prev, right: null }));
      setViewerImages((prev) => ({ ...prev, right: null }));
      setActiveViewerId("left");
    } else {
      setIsDualViewer(true);
      setActiveViewerId("right");
    }
  };

  const closeViewer = (viewerId: ViewerId) => {
    if (!isDualViewer) return;
    if (viewerId === "right") {
      setIsDualViewer(false);
      setViewerSlides((prev) => ({ ...prev, right: null }));
      setViewerImages((prev) => ({ ...prev, right: null }));
      setActiveViewerId("left");
    } else {
      setViewerSlides((prev) => ({ left: prev.right, right: null }));
      setViewerImages((prev) => ({ left: prev.right, right: null }));
      setIsDualViewer(false);
      setActiveViewerId("left");
    }
  };

  /** 종류별 목록 다시 불러오기 (그룹 변경·정보 수정 후) */
  const reloadMediaList = async (kind: ViewerMediaKind): Promise<any[]> => {
    if (!id) return [];
    if (kind === "slide") {
      const data = await getPostData(+id);
      const next: Slide[] = data?.slides ?? [];
      if (next.length > 0) setSlides(next);
      return next;
    }
    if (kind === "image") {
      const next = (await getImageList(+id)) ?? [];
      setPhotos(next);
      return next;
    }
    const next = await getGrossImageList(+id);
    setGrossPhotos(next);
    return next;
  };

  /**
   * 미디어를 그룹으로 이동/해제. newGroupName 이 오면 그 이름으로 새 그룹을 만들면서 배정한다.
   * (items 교체 API 는 다른 그룹 소속 미디어를 받지 않으므로 moveMediaToGroup 이 원 그룹에서 먼저 뺀다)
   */
  const handleGroupMove = async (
    kind: ViewerMediaKind,
    mediaIds: number[],
    targetGroupId: number | null,
    newGroupName?: string
  ) => {
    if (!id || mediaIds.length === 0) return;
    const mediaType = MEDIA_TYPE_OF[kind];
    // 소속 맵은 case 필터와 무관하게 게시글 전체 미디어 기준이어야 한다
    const membership = buildMembership(mediaListOf(kind), mediaType);

    if (newGroupName) {
      await moveMediaToGroup(mediaIds, null, membership);
      await createMediaGroup({
        postId: +id,
        mediaType,
        name: newGroupName,
        mediaIds,
      });
    } else {
      await moveMediaToGroup(mediaIds, targetGroupId, membership);
    }

    const [groups, newList] = await Promise.all([getMediaGroups(+id), reloadMediaList(kind)]);
    setMediaGroups(groups);
    message.success(
      targetGroupId == null && !newGroupName ? "그룹이 해제되었습니다." : "그룹이 변경되었습니다."
    );

    // 옮긴 미디어가 보고 있던 것이라면, 현재 필터에서 빠졌는지 확인해 이동시킨다
    if (
      kind === mediaTab &&
      activeKind === kind &&
      activeMediaId != null &&
      mediaIds.includes(activeMediaId) &&
      activeGroupFilter != null
    ) {
      const remaining = newList.filter((m) => matchesGroupFilter(m, activeGroupFilter));
      if (remaining.length === 0) {
        setActiveGroupFilter(null);
        if (newList.length > 0) handleMediaClick(newList[0]);
      } else {
        handleMediaClick(remaining[0]);
      }
    }
  };

  /** 칸의 이미지 어노테이션 목록 갱신 (뷰어 ↔ 사이드 탭 공유) */
  const setPaneAnnotations = useCallback(
    (viewerId: ViewerId, kind: "image" | "gross", mediaId: number, list: any[]) => {
      setViewerImages((prev) => {
        const target = prev[viewerId];
        if (!target || target.kind !== kind || target.id !== mediaId) return prev;
        return { ...prev, [viewerId]: { ...target, annotations: list } };
      });
    },
    [setViewerImages]
  );

  const handleGetMpp = async (postId: number, filename: string) => {
    return await getSlideMpp(postId, filename);
  };

  const handleGetAnnotations = async (slideId: number) => {
    const data = await getSlideData(slideId);
    return { annotations: data.annotations, slideComments: data.slideComments };
  };

  const makeSaveAnnotationHandler = useCallback(
    (viewerId: ViewerId) => async (slideId: number, path: any, name: string) => {
      await postAnnotation(slideId, path, name);
      const data = await getSlideData(slideId);
      setViewerSlides((prev) => {
        const target = prev[viewerId];
        if (!target || target.slideId !== slideId) return prev;
        return { ...prev, [viewerId]: { ...target, ...data } };
      });
    },
    [setViewerSlides]
  );

  useEffect(() => {
    const left = viewerSlides.left;
    const right = viewerSlides.right;
    if (!left && !right) return;
    setSlides((prevSlides) =>
      prevSlides.map((s) => {
        if (left && s.slideId === left.slideId) return { ...s, ...left };
        if (right && s.slideId === right.slideId) return { ...s, ...right };
        return s;
      })
    );
  }, [viewerSlides]);

  /** 현재 탭 목록(case·그룹 필터 적용)에서 이전/다음 미디어를 활성 칸에 띄운다. 끝에서는 반대쪽 끝으로 돈다. */
  const stepMedia = useCallback(
    (direction: "prev" | "next") => {
      if (filteredEntries.length === 0) return;

      const listElement = slideListRef.current;
      if (!listElement) return;

      // 활성 칸이 다른 종류를 보고 있으면 현재 탭 목록의 처음/끝부터 시작
      const posInFiltered =
        activeKind === mediaTab
          ? filteredEntries.findIndex(
              ({ item }) => mediaIdOfKind(item, mediaTab) === activeMediaId
            )
          : -1;

      if (direction === "prev") {
        const prevPos =
          posInFiltered <= 0 ? filteredEntries.length - 1 : posInFiltered - 1;
        const { item, originalIndex } = filteredEntries[prevPos];
        handleMediaClick(item);

        if (posInFiltered === 0) {
          const lastCard = slideCardRefs.current[originalIndex];
          if (lastCard) {
            listElement.scrollTo({ left: lastCard.offsetLeft, behavior: "smooth" });
          }
        } else {
          setTimeout(() => {
            const cardElement = slideCardRefs.current[originalIndex];
            if (cardElement) {
              const listRect = listElement.getBoundingClientRect();
              const cardRect = cardElement.getBoundingClientRect();
              if (cardRect.left < listRect.left) {
                listElement.scrollTo({
                  left: cardElement.offsetLeft - 20,
                  behavior: "smooth",
                });
              }
            }
          }, 0);
        }
      } else {
        const nextPos =
          posInFiltered >= filteredEntries.length - 1 ? 0 : posInFiltered + 1;
        const { item, originalIndex } = filteredEntries[nextPos];
        handleMediaClick(item);

        if (posInFiltered === filteredEntries.length - 1) {
          listElement.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          setTimeout(() => {
            const cardElement = slideCardRefs.current[originalIndex];
            if (cardElement) {
              const listRect = listElement.getBoundingClientRect();
              const cardRect = cardElement.getBoundingClientRect();
              if (cardRect.right > listRect.right) {
                listElement.scrollTo({
                  left:
                    cardElement.offsetLeft -
                    listElement.clientWidth +
                    cardElement.clientWidth +
                    20,
                  behavior: "smooth",
                });
              }
            }
          }, 0);
        }
      }
    },
    [filteredEntries, activeKind, activeMediaId, mediaTab, handleMediaClick]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;
      if (isInput) return;

      if (e.key === "ArrowLeft") stepMedia("prev");
      else if (e.key === "ArrowRight") stepMedia("next");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [stepMedia]);

  useEffect(() => {
    slideCardRefs.current = slideCardRefs.current.slice(0, tabList.length);
  }, [tabList.length]);

  useEffect(() => {
    const el = slideListRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  const renderViewerPane = (viewerId: ViewerId) => {
    const slide = viewerSlides[viewerId];
    const image = viewerImages[viewerId];
    const isActive = activeViewerId === viewerId;

    const activate = () => {
      if (activeViewerId !== viewerId) setActiveViewerId(viewerId);
    };

    const renderContent = () => {
      if (!currentPost?.id) return null;

      if (image) {
        const isGross = image.kind === "gross";
        return (
          <OSDImageViewer
            key={`${viewerId}-${image.kind}-${image.id}`}
            viewerId={viewerId}
            imagePath={image.pathFileName}
            photoId={image.id}
            isGrossFile={isGross}
            isAnno={true}
            isUserSet={true}
            showAnnotationButton={false}
            onActivate={activate}
            onGetAnnotations={isGross ? getGrossAnnotations : getImageAnnotations}
            onSaveAnnotation={isGross ? saveGrossAnnotation : saveImageAnnotation}
            externalAnnotations={image.annotations}
            onAnnotationsChange={(list) =>
              setPaneAnnotations(viewerId, image.kind, image.id, list)
            }
          />
        );
      }

      if (slide?.filename) {
        return (
          <OSDSlideViewer
            key={`${viewerId}-${slide.slideId}`}
            viewerId={viewerId}
            filename={slide.filename}
            postId={currentPost.id}
            slideId={slide.slideId}
            slideTitle={slide.slidename || ""}
            isAnno={true}
            onGetSlideMpp={handleGetMpp}
            onGetAnnotations={handleGetAnnotations}
            onSaveAnnotation={makeSaveAnnotationHandler(viewerId)}
            onActivate={activate}
            isReset={false}
            isUserSet={true}
            isSaveSet={false}
            isAnnotationButton={false}
            isCommentButton={false}
            useContextMenu={true}
          />
        );
      }

      return null;
    };

    const content = renderContent();

    return (
      <ViewerPane
        key={viewerId}
        $isActive={isActive && isDualViewer}
        onClick={activate}
      >
        {isDualViewer && (
          <ViewerPaneHeader>
            <ViewerPaneLabel
              $isActive={isActive}
              onClick={(e) => {
                e.stopPropagation();
                activate();
              }}
            >
              {viewerId === "left" ? "Viewer A" : "Viewer B"}
              {isActive && <ActiveDot />}
            </ViewerPaneLabel>
            <CloseViewerButton
              onClick={(e) => {
                e.stopPropagation();
                closeViewer(viewerId);
              }}
              title="이 뷰어 닫기"
            >
              <CloseOutlined />
            </CloseViewerButton>
          </ViewerPaneHeader>
        )}

        {/* 이전/다음 미디어 화살표 (활성 칸에만. 키보드 ←/→ 와 같은 동작) */}
        {isActive && content && filteredEntries.length > 1 && (
          <>
            <MediaStepButton
              type="button"
              $side="left"
              title="이전 (←)"
              onClick={(e) => {
                e.stopPropagation();
                stepMedia("prev");
              }}
            >
              <LeftOutlined />
            </MediaStepButton>
            <MediaStepButton
              type="button"
              $side="right"
              title="다음 (→)"
              onClick={(e) => {
                e.stopPropagation();
                stepMedia("next");
              }}
            >
              <RightOutlined />
            </MediaStepButton>
          </>
        )}

        {content ?? (
          <EmptyViewer onClick={activate}>
            {isDualViewer
              ? "하단 목록에서 선택하세요"
              : "No Media Selected"}
          </EmptyViewer>
        )}
      </ViewerPane>
    );
  };

  const showCaseBar = cases.length > 0;
  const groupModalKind = groupChangeTarget?.kind ?? mediaTab;
  const showTabBar = availableKinds.length > 1;
  /**
   * 듀얼 뷰어 버튼을 붙일 줄. 목록 영역 높이를 줄이려고 따로 줄을 만들지 않고
   * 탭 줄 → Case 줄 → 그룹 줄 중 가장 위에 있는 줄의 오른쪽 끝에 둔다.
   */
  const dualButtonRow: "tab" | "case" | "group" | "own" = showTabBar
    ? "tab"
    : showCaseBar
    ? "case"
    : hasGroups
    ? "group"
    : "own";

  const dualToggleButton = (
    <DualToggleWrapper>
      <Tooltip
        title={
          isDualViewer
            ? "싱글 뷰어로 전환"
            : "듀얼 뷰어 열기 (또는 목록 항목을 Ctrl/⌘+클릭)"
        }
      >
        <Button
          size="small"
          type={isDualViewer ? "primary" : "default"}
          icon={<SplitCellsOutlined />}
          onClick={toggleDualViewer}
        >
          {isDualViewer ? "듀얼 ON" : "듀얼 뷰어"}
        </Button>
      </Tooltip>
      {isDualViewer && (
        <Tooltip title="양쪽 뷰어를 같은 배율로 맞춰 함께 확대/축소합니다">
          <Button
            size="small"
            type={isZoomSync ? "primary" : "default"}
            icon={<LinkOutlined />}
            onClick={() => setIsZoomSync((prev) => !prev)}
          >
            줌 동기화
          </Button>
        </Tooltip>
      )}
    </DualToggleWrapper>
  );

  return (
    <ViewerContext.Provider
      value={{
        userCustomSet,
        setUserCustomSet,
        activePanel,
        setActivePanel,
        togglePanel: (panelName: string) =>
          setActivePanel((prev) => (prev === panelName ? null : panelName)),
      }}
    >
      <ViewerContainer>
        <ViewerArea $isDual={isDualViewer}>
          {renderViewerPane("left")}
          {isDualViewer && renderViewerPane("right")}
        </ViewerArea>

        <SlideListArea $isOpen={isSlideListOpen}>
          <SlideListToggleButton
            onClick={() => setIsSlideListOpen(!isSlideListOpen)}
            $isOpen={isSlideListOpen}
          >
            {isSlideListOpen ? <DownOutlined /> : <UpOutlined />}
          </SlideListToggleButton>

          <SlideListWrapper $isOpen={isSlideListOpen}>
            {/* 두 종류 이상 있을 때만 Slide / Image / Gross 탭 노출 */}
            {showTabBar && (
              <MediaTabBar>
                <Segmented
                  size="small"
                  value={mediaTab}
                  onChange={(v) => handleTabChange(v as ViewerMediaKind)}
                  options={availableKinds.map((k) => ({
                    value: k,
                    label: `${MEDIA_TAB_LABEL[k]} ${countInCase(k)}`,
                  }))}
                />
                {dualButtonRow === "tab" && dualToggleButton}
              </MediaTabBar>
            )}

            {showCaseBar && (
              <CaseFilterBar>
                <FilterLabel>Case</FilterLabel>
                <Tag
                  color={activeCaseId === null ? "blue" : "default"}
                  onClick={() => handleCaseFilterChange(null)}
                  style={{ cursor: "pointer" }}
                >
                  전체{" "}
                  <SlideCountBadge $active={activeCaseId === null}>
                    {tabList.length}
                  </SlideCountBadge>
                </Tag>
                {cases.map((pc, idx) => {
                  const count = tabList.filter(
                    (m: any) => m.postCaseId === pc.postCaseId
                  ).length;
                  const active = activeCaseId === pc.postCaseId;
                  return (
                    <Tag
                      key={pc.postCaseId}
                      color={active ? "blue" : "default"}
                      onClick={() => handleCaseFilterChange(pc.postCaseId)}
                      style={{ cursor: "pointer" }}
                    >
                      {postCaseDisplayName(pc, idx)}{" "}
                      <SlideCountBadge $active={active}>{count}</SlideCountBadge>
                    </Tag>
                  );
                })}
                {dualButtonRow === "case" && dualToggleButton}
              </CaseFilterBar>
            )}

            {hasGroups && (
              <GroupFilterBar>
                <Tag
                  color={activeGroupFilter == null ? "blue" : "default"}
                  onClick={() => handleGroupFilterChange(null)}
                  style={{ cursor: "pointer" }}
                >
                  All{" "}
                  <SlideCountBadge $active={activeGroupFilter == null}>
                    {groupCounts.total}
                  </SlideCountBadge>
                </Tag>
                {groupCounts.ungrouped > 0 && (
                  <Tag
                    color={activeGroupFilter === UNGROUPED_FILTER ? "blue" : "default"}
                    onClick={() =>
                      handleGroupFilterChange(
                        activeGroupFilter === UNGROUPED_FILTER ? null : UNGROUPED_FILTER
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    그룹없음{" "}
                    <SlideCountBadge $active={activeGroupFilter === UNGROUPED_FILTER}>
                      {groupCounts.ungrouped}
                    </SlideCountBadge>
                  </Tag>
                )}
                {tabGroups.map((group) => {
                  const gid = group.mediaGroupId;
                  const isActive = activeGroupFilter === gid;
                  return (
                    <Tag
                      key={gid}
                      color={isActive ? "blue" : "default"}
                      onClick={() => handleGroupFilterChange(isActive ? null : gid)}
                      style={{ cursor: "pointer" }}
                    >
                      {group.name}{" "}
                      <SlideCountBadge $active={isActive}>
                        {groupCounts.byGroup[gid] ?? 0}
                      </SlideCountBadge>
                    </Tag>
                  );
                })}
                {dualButtonRow === "group" && dualToggleButton}
              </GroupFilterBar>
            )}

            {/* 위 줄이 하나도 없을 때만 듀얼 뷰어 버튼용 줄을 따로 둔다 */}
            {dualButtonRow === "own" && <DualOnlyBar>{dualToggleButton}</DualOnlyBar>}

            <SlideListContent ref={slideListRef}>
              {filteredEntries.map(({ item, originalIndex: index }) => {
                const itemId = mediaIdOfKind(item, mediaTab)!;
                const isLoadedOn = (vid: ViewerId) =>
                  mediaTab === "slide"
                    ? viewerSlides[vid]?.slideId === itemId
                    : viewerImages[vid]?.kind === mediaTab && viewerImages[vid]?.id === itemId;
                const loadedOnLeft = isLoadedOn("left");
                const loadedOnRight = isLoadedOn("right");
                const isActiveItem = activeKind === mediaTab && activeMediaId === itemId;
                const itemName = mediaNameOf(item, mediaTab);

                const menuItems: MenuProps["items"] = [
                  ...(isDualViewer
                    ? [
                        {
                          key: "loadLeft",
                          label: "왼쪽 뷰어에 열기",
                          onClick: () => loadMediaIntoViewer(item, mediaTab, "left"),
                        },
                        {
                          key: "loadRight",
                          label: "오른쪽 뷰어에 열기",
                          onClick: () => loadMediaIntoViewer(item, mediaTab, "right"),
                        },
                        { type: "divider" as const },
                      ]
                    : []),
                  ...(isAdmin
                    ? [
                        {
                          key: "changeGroup",
                          label: "그룹 지정",
                          icon: <SwapOutlined />,
                          onClick: () => setGroupChangeTarget({ kind: mediaTab, id: itemId }),
                        },
                      ]
                    : []),
                  // 그룹에 속해 있을 때만 해제 메뉴를 띄운다
                  ...(isAdmin && groupIdOf(item) != null
                    ? [
                        {
                          key: "clearGroup",
                          label: "그룹 해제",
                          icon: <MinusCircleOutlined />,
                          onClick: () => handleGroupMove(mediaTab, [itemId], null),
                        },
                      ]
                    : []),
                ];

                const card = (
                  <SlideCard
                    key={`${mediaTab}-${itemId}`}
                    ref={(el) => (slideCardRefs.current[index] = el)}
                    $isActive={isActiveItem}
                    onClick={(e) => handleMediaClickWithScroll(item, index, e)}
                  >
                    <SlideCardInner $isActive={isActiveItem}>
                      <SlideThumbnailWrapper>
                        <SlideThumbnail
                          src={mediaThumbOf(item, mediaTab)}
                          alt={itemName}
                          loading="lazy"
                        />
                        {isDualViewer && (loadedOnLeft || loadedOnRight) && (
                          <LoadedBadges>
                            {loadedOnLeft && <LoadedBadge $side="left">A</LoadedBadge>}
                            {loadedOnRight && <LoadedBadge $side="right">B</LoadedBadge>}
                          </LoadedBadges>
                        )}
                      </SlideThumbnailWrapper>
                      <SlideFooter>
                        <Tooltip title={itemName} placement="top">
                          <SlideName>{itemName}</SlideName>
                        </Tooltip>
                      </SlideFooter>
                    </SlideCardInner>
                  </SlideCard>
                );

                return menuItems.length > 0 ? (
                  <Dropdown
                    key={`${mediaTab}-${itemId}`}
                    menu={{ items: menuItems }}
                    trigger={["contextMenu"]}
                    overlayStyle={{ minWidth: 180 }}
                  >
                    {card}
                  </Dropdown>
                ) : (
                  card
                );
              })}
            </SlideListContent>
          </SlideListWrapper>
        </SlideListArea>

        <SidePanel $isOpen={activePanel !== null}>
          {/* 활성 칸이 이미지면 이미지용 사이드 탭 (어노테이션만) */}
          {activeImage ? (
            <ImageSideMenu
              activePanel={activePanel}
              onClose={() => setActivePanel(null)}
            />
          ) : (
            <SideMenuSample2
              activePanel={activePanel}
              onClose={() => setActivePanel(null)}
              postWriter={currentPost?.writer}
              postId={currentPost?.id}
            />
          )}
        </SidePanel>

        <MiniBar>
          {/* 정보·코멘트 탭은 슬라이드 전용 (이미지는 어노테이션 탭만) */}
          {!activeImage && (
            <MiniBarButton
              $active={activePanel === "info"}
              onClick={() => setActivePanel((prev) => (prev === "info" ? null : "info"))}
              title="Information"
            >
              <InfoCircleOutlined />
            </MiniBarButton>
          )}
          <MiniBarButton
            $active={activePanel === "annotation"}
            onClick={() => setActivePanel((prev) => (prev === "annotation" ? null : "annotation"))}
            title="Annotations"
          >
            <FileImageOutlined />
          </MiniBarButton>
          {!activeImage && (
            <MiniBarButton
              $active={activePanel === "comment"}
              onClick={() => setActivePanel((prev) => (prev === "comment" ? null : "comment"))}
              title="Comments"
            >
              <CommentOutlined />
            </MiniBarButton>
          )}
        </MiniBar>
      </ViewerContainer>

      <MediaGroupAssignModal
        open={groupChangeTarget != null}
        groups={groupsOf(groupModalKind)}
        selectedCount={1}
        itemLabel={MEDIA_ITEM_LABEL[groupModalKind]}
        allowUngroup={false}
        currentGroupId={groupIdOf(
          mediaListOf(groupModalKind).find(
            (m) => mediaIdOfKind(m, groupModalKind) === groupChangeTarget?.id
          )
        )}
        onCancel={() => setGroupChangeTarget(null)}
        onSubmit={async ({ targetGroupId, newGroupName }) => {
          if (groupChangeTarget == null) return;
          await handleGroupMove(
            groupChangeTarget.kind,
            [groupChangeTarget.id],
            targetGroupId,
            newGroupName
          );
          setGroupChangeTarget(null);
        }}
      />
    </ViewerContext.Provider>
  );
};

const MediaTabBar = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 16px 4px;
  flex-shrink: 0;
`;

const ViewerContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-template-rows: minmax(0, 1fr) auto;
  height: calc(100vh - 30px);
  overflow: hidden;
  position: relative;
`;

const ViewerArea = styled.div<{ $isDual: boolean }>`
  grid-column: 1;
  grid-row: 1;
  display: grid;
  grid-template-columns: ${({ $isDual }) => ($isDual ? "1fr 1fr" : "1fr")};
  gap: ${({ $isDual }) => ($isDual ? "2px" : "0")};
  background-color: #1e3a52;
  overflow: hidden;
  position: relative;
`;

const ViewerPane = styled.div<{ $isActive: boolean }>`
  position: relative;
  overflow: hidden;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  outline: ${({ $isActive }) =>
    $isActive ? "2px solid #1890ff" : "2px solid transparent"};
  outline-offset: -2px;
  transition: outline-color 0.2s;
`;

const ViewerPaneHeader = styled.div`
  position: absolute;
  bottom: 5px;
  left: 8px;
  z-index: 1500;
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: none;
`;

const ViewerPaneLabel = styled.div<{ $isActive: boolean }>`
  background: ${({ $isActive }) =>
    $isActive ? "rgba(24, 144, 255, 0.9)" : "rgba(0, 0, 0, 0.55)"};
  color: #fff;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: auto;
  border: 1px solid rgba(255, 255, 255, 0.25);
`;

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
  display: inline-block;
`;

const CloseViewerButton = styled.button`
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;

  &:hover {
    background: rgba(255, 77, 79, 0.85);
  }
`;

const MediaStepButton = styled.button<{ $side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === "left" ? "left: 12px;" : "right: 12px;")}
  transform: translateY(-50%);
  z-index: 1500;
  width: 36px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.15s, background 0.15s;

  &:hover {
    opacity: 1;
    background: rgba(24, 144, 255, 0.85);
  }
`;

const EmptyViewer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #fff;
  background: #0a1929;
`;

const DualToggleWrapper = styled.div`
  display: flex;
  gap: 6px;
  margin-left: auto;
  padding-right: 4px;
`;

const DualOnlyBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 16px 6px;
  flex-shrink: 0;
`;

const LoadedBadges = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  display: flex;
  gap: 3px;
`;

const LoadedBadge = styled.span<{ $side: "left" | "right" }>`
  background: ${({ $side }) => ($side === "left" ? "#1890ff" : "#52c41a")};
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.4;
`;

const SlideListArea = styled.div<{ $isOpen: boolean }>`
  grid-column: 1 / 4;
  grid-row: 2;
  border-top: ${({ $isOpen }) => ($isOpen ? "1px solid #1e3a52" : "none")};
  background-color: #fff;
  display: flex;
  flex-direction: column;
  max-height: ${({ $isOpen }) => ($isOpen ? "50vh" : "0px")};
  overflow: visible;
  transition: max-height 0.3s ease-out;
  position: relative;
`;

const CaseFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 16px 4px;
  align-items: center;
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

const FilterLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: #595959;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-right: 4px;
`;

const GroupFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 16px 6px;
  align-items: center;
  flex-shrink: 0;
`;

const SlideCountBadge = styled.span<{ $active: boolean }>`
  display: inline-block;
  background: ${({ $active }) =>
    $active ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.08)"};
  border-radius: 8px;
  padding: 0 5px;
  font-size: 11px;
  margin-left: 3px;
  line-height: 1.4;
`;

const SlideListToggleButton = styled.button<{ $isOpen: boolean }>`
  position: absolute;
  top: -28px;
  right: 6px;
  background-color: #8899aa;
  border: 1px solid #1e3a52;
  border-bottom: ${({ $isOpen }) => ($isOpen ? "none" : "1px solid #1e3a52")};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  color: #051e34;
  padding: 6px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  z-index: 1000;
  transition: all 0.3s ease-out;

  svg {
    font-size: 12px;
  }

  &:hover {
    background-color: #e8f0f7;
  }
`;

const SlideListWrapper = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.2s ease-out;
`;

const SlideListContent = styled.div`
  padding: 8px 1rem 12px;
  display: flex;
  align-items: center;
  gap: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  box-sizing: border-box;
  flex-shrink: 0;

  @media (max-width: 1920px) {
    gap: 10px;
  }

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #b3c3d1;
  }

  &::-webkit-scrollbar-thumb {
    background: #1e3a52;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #2d5a7b;
  }
`;

const SlideCard = styled.div<{ $isActive: boolean }>`
  flex-shrink: 0;
  width: 8rem;
  cursor: pointer;
  position: relative;

  @media (max-width: 1920px) {
    width: 6.5rem;
  }

  @media (min-width: 2560px) {
    width: 8.5rem;
  }
`;

const SlideCardInner = styled.div<{ $isActive: boolean }>`
  background-color: #ffffff;
  border: 2px solid ${(props) => (props.$isActive ? "#1890ff" : "#e0e0e0")};
  overflow: hidden;
  transition: border-color 0.2s;
  /* 번호·확장자 줄을 없앤 만큼 줄인 높이 (썸네일 영역 크기는 그대로) */
  height: 7.625rem;
  display: flex;
  flex-direction: column;

  @media (max-width: 1920px) {
    height: 5.25rem;
  }

  @media (min-width: 2560px) {
    height: 8.125rem;
  }

  &:hover {
    border-color: #1890ff;
  }
`;

const SlideThumbnailWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  padding: 8px;
  min-height: 0;
  position: relative;

  @media (max-width: 1920px) {
    padding: 4px;
  }
`;

const SlideThumbnail = styled.img`
  max-height: 100%;
  object-fit: contain;
`;

const SlideFooter = styled.div`
  padding: 6px 8px;
  background-color: #f5f5f5;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
  min-height: 28px;

  @media (max-width: 1920px) {
    padding: 3px 6px;
    min-height: 20px;
  }
`;

const SlideName = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
`;

const SidePanel = styled.div<{ $isOpen: boolean }>`
  grid-column: 2;
  grid-row: 1;
  width: ${({ $isOpen }) => ($isOpen ? "450px" : "0")};
  background-color: #0a1929;
  border-left: ${({ $isOpen }) => ($isOpen ? "1px solid #1e3a52" : "none")};
  overflow: hidden;
  transition: width 0.3s ease-out;
  flex-shrink: 0;
`;

const MiniBar = styled.div`
  grid-column: 3;
  grid-row: 1;
  width: 60px;
  background-color: #051e34;
  border-left: 1px solid #1e3a52;
  display: flex;
  flex-direction: column;
  padding: 15px 0;
  gap: 8px;
  flex-shrink: 0;
`;

const MiniBarButton = styled.button<{ $active?: boolean }>`
  background-color: ${({ $active }) => ($active ? "#1890ff" : "transparent")};
  border: none;
  color: ${({ $active }) => ($active ? "#fff" : "#8899aa")};
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 6px;
  margin: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }

  &:hover {
    background-color: ${({ $active }) => ($active ? "#1890ff" : "#1e3a52")};
    color: #fff;
  }
`;

export default Viewer;